import React, { useState, useRef } from "react";
import { useAuth } from "@/lib/auth";
import { api, compressImage, toImageSrc, formatApiErrorDetail, DEFAULT_AVATAR_URL } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user, updateMe } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
    city: user?.city || "",
    area: user?.area || "",
    specialty: user?.specialty || "Residential",
    phone: user?.phone || "",
    license_no: user?.license_no || "",
    avatar_url: user?.avatar_url || "",
    years_experience: user?.years_experience || 0,
  });
  const [saving, setSaving] = useState(false);

  if (!user || typeof user !== "object") return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onAvatarPicked = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Use JPG, PNG, or WebP");
      return;
    }
    setUploading(true);
    try {
      const compressed = await compressImage(file, 512, 0.85);
      if (compressed.size > 5 * 1024 * 1024) {
        toast.error("Image too large (max 5 MB)");
        return;
      }
      const fd = new FormData();
      fd.append("file", compressed);
      const { data } = await api.post("/upload", fd);
      // Persist immediately so the avatar shows everywhere in the app right away.
      const next = `${toImageSrc(data.path)}`;
      await updateMe({ ...form, avatar_url: next, years_experience: Number(form.years_experience || 0) });
      set("avatar_url", next);
      toast.success("Profile picture updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const clearAvatar = async () => {
    setUploading(true);
    try {
      await updateMe({ ...form, avatar_url: "", years_experience: Number(form.years_experience || 0) });
      set("avatar_url", "");
      toast.success("Reset to default icon");
    } catch (e) {
      toast.error("Failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateMe({ ...form, years_experience: Number(form.years_experience) });
      toast.success("Profile updated");
    } catch (e) {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  const displaySrc = form.avatar_url ? toImageSrc(form.avatar_url) : DEFAULT_AVATAR_URL;

  return (
    <div className="min-h-screen p-6 md:p-10 pb-24" data-testid="profile-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Your profile</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight">How others see you.</h1>
        </div>

        <div className="card-surface rounded-xl p-6 md:p-8">
          <div className="flex items-center gap-5 pb-6 border-b hairline">
            <div className="relative group">
              <Avatar className="h-24 w-24 border-2 border-gold/30">
                <AvatarImage src={displaySrc} data-testid="profile-avatar-preview" />
                <AvatarFallback className="bg-ink-700 text-2xl">{form.name?.[0]}</AvatarFallback>
              </Avatar>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="upload-avatar-button"
                className="absolute -bottom-1 -right-1 h-9 w-9 rounded-full bg-gold text-ink-900 flex items-center justify-center shadow-lg hover:bg-gold-light transition-colors disabled:opacity-60"
                title="Upload profile picture"
                aria-label="Upload profile picture"
              >
                <Camera size={16} weight="fill" />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => { onAvatarPicked(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="profile-avatar-input"
              />
            </div>
            <div className="flex-1">
              <div className="text-lg">{form.name}</div>
              <div className="text-sm text-zinc-500">{user.email}</div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="text-xs text-gold hover:text-gold-light disabled:opacity-50"
                  data-testid="profile-avatar-change-link"
                >
                  {uploading ? "Uploading…" : (form.avatar_url ? "Change picture" : "Upload picture")}
                </button>
                {form.avatar_url && (
                  <>
                    <span className="text-zinc-600">·</span>
                    <button
                      type="button"
                      onClick={clearAvatar}
                      disabled={uploading}
                      className="text-xs text-zinc-400 hover:text-red-400 flex items-center gap-1 disabled:opacity-50"
                      data-testid="profile-avatar-clear"
                    >
                      <Trash size={12} /> Use default
                    </button>
                  </>
                )}
              </div>
              <div className="text-[10px] text-zinc-600 mt-1.5">JPG, PNG, or WebP · max 5 MB</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <Field label="Name">
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-ink-700 border-white/10" data-testid="profile-name" />
            </Field>
            <Field label="City">
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-ink-700 border-white/10" data-testid="profile-city" placeholder="Mumbai" />
            </Field>
            <Field label="Area">
              <Input value={form.area} onChange={(e) => set("area", e.target.value)} className="bg-ink-700 border-white/10" data-testid="profile-area" placeholder="Bandra West" />
            </Field>
            <Field label="Specialty">
              <Select value={form.specialty} onValueChange={(v) => set("specialty", v)}>
                <SelectTrigger className="bg-ink-700 border-white/10" data-testid="profile-specialty"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Residential">Residential</SelectItem>
                  <SelectItem value="Luxury">Luxury</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Years Experience">
              <Input type="number" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} className="bg-ink-700 border-white/10 font-mono" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-ink-700 border-white/10" />
            </Field>
            <Field label="License #">
              <Input value={form.license_no} onChange={(e) => set("license_no", e.target.value)} className="bg-ink-700 border-white/10" />
            </Field>
            <Field label="Bio" wide>
              <Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={4} className="bg-ink-700 border-white/10" data-testid="profile-bio" />
            </Field>
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-gold text-ink-900 hover:bg-gold-light" data-testid="save-profile">
              {saving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
